import { Alert } from '@repo/ui'

import { useIntroData } from 'hooks/api'
import { ClearSampleDataButton } from '../ClearSampleDataButton'

/**
 * The sample-data caveat (VRG-512). Mounted on every surface the intro seed writes to,
 * which is now nine of them, not just Analytics: the copy is written to be true of a
 * chart page and of a plain list, and the surface names its own rows via `nouns`.
 *
 * ── This banner is not decoration; it is the other half of a decision ───────────────
 * `example-kind-coverage.ts` puts every analytics read path in `INTRO_CONDITIONAL`:
 * charts and tiles COUNT seeded rows ONLY WHILE the tenant has no real certificate of
 * its own. Filtering them out during onboarding would put the empty state back in
 * front of exactly the user the intro set exists to serve, and any tour step
 * spotlighting a chart would ship broken — so while that is true, the page counts the
 * samples and this banner says so.
 *
 * ── ⚠️ REVISED, NOT OBEYED: the old instruction here was wrong for what actually
 * changed ────────────────────────────────────────────────────────────────────────────
 * This docblock used to say "if the analytics paths are ever flipped to
 * `INTRO_EXCLUDED`, delete this banner in the same change." That was written for a
 * disposition that was a CONSTANT (always inclusive, or always excluded). What
 * shipped instead is a disposition that is a FUNCTION of the tenant's own data: every
 * `INTRO_CONDITIONAL` path counts samples until a real certificate exists, then stops
 * — automatically, mid-tenant-lifetime, with no code deploy in between. So the banner
 * is not a thing to delete on some future flip; it is a thing that must announce BOTH
 * states, because both are real and both are currently reachable by the same tenant a
 * week apart. Deleting it would leave the moment described in point 2 below
 * unexplained.
 *
 * Two states, driven by `useIntroData()`'s `hasRealData` (a client-visible reflection
 * of the exact same `resolveIntroExclusion` predicate the server applies):
 *
 *   1. `hasIntroData && !hasRealData` — the onboarding state. Figures INCLUDE the
 *      seeded rows; the banner says so, so a brand never repeats a fabricated number
 *      to their own stakeholders without knowing it.
 *   2. `hasIntroData && hasRealData` — the transition state. A real certificate now
 *      exists, so every analytics aggregate has ALREADY stopped counting the sample
 *      rows — this is the moment a KPI (e.g. a scan count) can visibly DROP with no
 *      user action in between. The banner exists here specifically to explain that
 *      drop rather than let it look like data loss. Samples may still be sitting in
 *      the database (clearing is a separate, user-initiated action), which is why
 *      "clear sample data" stays offered in both states rather than only the first.
 *
 * ── Known limit, stated rather than hidden ─────────────────────────────────────────
 * A screenshot of a single chart loses the banner. That is the honest cost of the
 * page-level treatment over per-figure marking, which was the runner-up option; it was
 * not chosen because marking ~25 aggregates clutters the surface a tour is trying to
 * showcase. If screenshots-out-of-context turn out to matter, per-figure marking is
 * the escalation.
 */
export interface IntroDataBannerProps {
  /**
   * Does the surface rendering this banner ACTUALLY have sample rows on it right now?
   *
   * Defaults to `true`, which is the honest answer for every surface whose list is a
   * direct read of the tenant's own tables — Analytics, Products, Certificates, People,
   * Tag inventory, Address book, Sales. There, "the tenant holds intro data" and "this
   * page is showing you some" are the same question, and `hasIntroData` answers it.
   *
   * The internal Orders list is the exception this prop exists for, and it is worth
   * being precise about why rather than treating it as a generic escape hatch. That
   * list is served from the live Shopify Admin API when a store is connected, and only
   * falls back to local `orders` rows when Shopify returns none
   * (`getLocalOrdersFallback`). A Shopify-sourced order is real by construction — every
   * mapper sets `example_kind: null` explicitly — so a tenant can hold intro data while
   * every row on screen belongs to somebody who actually placed an order. Rendering the
   * banner off `hasIntroData` alone there would caption real orders as samples, which is
   * a worse failure than staying silent.
   *
   * Pass `false` (not `undefined`) while the answer is still unknown. `undefined` means
   * "no extra condition" and shows the banner; a caller that forwards a loading-state
   * `undefined` would flash it in and back out again.
   */
  surfaceHasSamples?: boolean

  /**
   * What the samples ON THIS SURFACE actually are, as a bare plural noun phrase that
   * reads after the word "sample": "sample addresses", "sample orders".
   *
   * Defaults to `products, certificates and scans` — the intro set's headline nouns and
   * the only ones that are true of Analytics, Products, Certificates and People. It is
   * a PROP rather than a route switch inside this component (§28/§47) because the page
   * is the only thing that knows what its own rows are; a `useLocation()` branch here
   * would put nine surfaces' vocabulary in a file none of them own, and would silently
   * fall through to the wrong noun the first time one of them moved.
   *
   * ⚠️ The noun is deliberately NOT threaded into the transition state's counting
   * clause, and that asymmetry is the point rather than an oversight. Only ANALYTICS
   * read paths are `INTRO_CONDITIONAL` (`example-kind-coverage.ts`), and the four
   * tables they count are products, certificates, owners and scans. Addresses, tags,
   * sales and orders were never in a chart, so "sample addresses are no longer counted
   * in your charts and totals" would answer today's inaccuracy with tomorrow's. The
   * second state therefore says what genuinely changed (the figures stopped counting
   * sample data) separately from what is still on screen (these sample <nouns>).
   */
  nouns?: string
}

export const IntroDataBanner = ({
  surfaceHasSamples = true,
  nouns = 'products, certificates and scans',
}: IntroDataBannerProps) => {
  const { hasIntroData, hasRealData, isReady } = useIntroData()

  // `isReady` gates on the query having SETTLED, not on `hasIntroData` being truthy.
  // Rendering off a bare `hasIntroData` would flash the banner in after the response
  // lands and shove the whole page down (the tri-state note in `useIntroData`).
  if (!isReady || !hasIntroData) return null
  // Tenant-level yes, surface-level no: the samples exist but this list is not one of
  // the places they show up (see the prop doc).
  if (!surfaceHasSamples) return null

  return (
    <Alert
      severity="info"
      variant="outlined"
      /*
        Both strings have to be true on a CHART page and on a plain LIST, because this
        component is mounted on both (Analytics has no list; Address Book, Sales, Tag
        inventory and the internal Orders list have no chart and no total).

        First state: "included in the lists and figures on this page" is a union, and a
        union is safe here because the claim is POSITIVE — the samples show up in
        whichever of the two the page actually has, so a page missing one half asserts
        nothing false.

        Second state: the same union would flip to a NEGATIVE claim ("no longer in the
        lists and figures") and become flatly wrong, because a list surface never
        filtered the seeded rows at all — only analytics paths are `INTRO_CONDITIONAL`,
        so on Address Book the sample rows are still sitting right under this banner.
        Hence the split: the counting change is stated about "your charts and totals"
        (unscoped from this page, since on eight of the nine surfaces it happened
        somewhere else), and what is still on screen is stated separately.

        "and may look lower than before" is subordinate to "charts and totals" rather
        than the standalone "Some numbers may look lower" it replaces, so it lands only
        where numbers exist instead of being noise on a list of addresses.

        §15: commas and separate sentences, no em-dashes.
      */
      title={
        hasRealData
          ? 'Your sample data is no longer counted'
          : 'This page includes sample data'
      }
      description={
        hasRealData
          ? 'You have added real certificates, so your charts and totals now count ' +
            'only your own data and may look lower than before. Your sample ' +
            `${nouns} are still here. Clear them whenever you like.`
          : `Your console was set up with sample ${nouns} so you could see how ` +
            'everything works. They are included in the lists and figures on this ' +
            'page. Clear them whenever you like.'
      }
      /*
        The SHARED clear control (§36), not a local button plus a local modal. Home now
        offers the same action, and that second call site is what turned this nine-line
        flow into a component: the confirm, the loading state and the
        leave-the-modal-open-on-failure behaviour are decisions that must not exist
        twice. Only the consequence copy stays here, because only this page knows what
        the reader is looking at.
      */
      button={
        <ClearSampleDataButton
          /*
            The consequence names what actually disappears, and which of the two states
            above the reader is in. "Are you sure?" would tell them nothing they did not
            already know (§43).

            SCOPE FIRST, because it is the part a reader can get wrong to their cost.
            `clear-intro-set.use-case.ts` deletes the WHOLE intro set for the company in
            one transaction: scans, transfers, certificates, products, tags, owners,
            pending sales, addresses and orders. It is not scoped to the surface the
            button was pressed on. So someone clearing from Settings -> Address Book,
            where the visible samples are three addresses, is also deleting six sample
            products and 188 scans they cannot see from here. The previous copy named
            "products, their certificates and their scan history" on every surface, which
            was the right SET described with the wrong nouns for four of the nine mount
            sites, and never said the action reached past the page at all.

            The effect clause follows the split the banner body uses: state 1 can speak of
            "lists and figures" because the claim is positive and the union is safe, while
            state 2 has to be specific, since "figures will drop" is false once analytics
            has already stopped counting samples.
          */
          consequence={
            hasRealData
              ? `Every sample record will be deleted, not only the ${nouns} on this ` +
                'page. Your own data is not affected. Your charts and totals already ' +
                'count only your real data, so this clears the leftover samples from ' +
                'your lists.'
              : `Every sample record will be deleted, not only the ${nouns} on this ` +
                'page. Your own data is not affected. Lists and figures across the ' +
                'console will stop showing the samples.'
          }
        />
      }
      sx={{ mb: 6 }}
    />
  )
}
